import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { guidePathForSlug } from '../Guides/guidePaths.js';
import { Link, Navigate, useParams, useLocation } from 'react-router-dom';
import GhostContentAPI from '@tryghost/content-api';
import { setHeroTitle, setHeroContent, setHeroImage } from '../../redux/hero.slice';
import Article from './Article';
import ViewArticle from './ViewArticle';
import PulseMetadata from './PulseMetadata';
import Spinner from '../../shared/Icon/Spinner';
import RelatedPulseLinks from '../../shared/RelatedPulseLinks';
import './Blog.css';

const api = new GhostContentAPI({
  url: 'https://blogadmin.shuttleops.com',
  key: '4cfffa548d3e48f0a47d440703',
  version: 'v6.0',
});

const guides = [
  { path: '/guides/first-club-session', title: 'Run your first club session' },
  { path: '/guides/badminton-pegboard', title: 'Badminton pegboard guide' },
  { path: '/guides/collect-badminton-club-payments', title: 'Collect badminton club payments' },
  { path: '/guides/club-attendance-register', title: 'Club attendance register and free template' },
  { path: '/guides/badminton-club-management-software', title: 'Badminton club management software guide' },
  { path: '/guides/switch-from-spond', title: 'Switch from Spond' },
  { path: '/guides/switch-from-playwaze', title: 'Switch from Playwaze' },
  { path: '/guides/switch-from-justgo-to-shuttleops', title: 'Switch from JustGo' },
];
const comparisons = [
  { path: '/compare', title: 'All software comparisons' },
  { path: '/compare/spond-alternative', title: 'ShuttleOps vs Spond' },
  { path: '/compare/playwaze-alternative', title: 'ShuttleOps vs Playwaze' },
  { path: '/compare/sportmember-alternative', title: 'ShuttleOps vs SportMember' },
  { path: '/compare/justgo-alternative', title: 'ShuttleOps vs JustGo' },
  { path: '/calculator', title: 'Club value calculator' },
];

function ArticleCollection() {
  const [articles, setArticles] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    Promise.all([
      api.posts.browse({ filter: 'featured:true', limit: 1, include: 'tags,authors' }),
      api.posts.browse({ limit: 10, page, include: 'tags,authors' }),
    ]).then(([featuredPosts, posts]) => {
      if (!active) return;
      const featuredPost = featuredPosts[0] || null;
      setFeatured(featuredPost);
      setArticles(previous => {
        const combined = [...(page === 1 ? [] : previous), ...posts];
        return [...new Map(combined.filter(post => post.id !== featuredPost?.id).map(post => [post.id, post])).values()];
      });
      setHasMore(Boolean(posts.meta.pagination.next));
    }).catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [page, retry]);

  return (
    <div className="pulse-collection">
      <nav className="pulse-browse" aria-label="Browse Pulse resources">
        <div className="pulse-browse-top"><h2>Browse Pulse</h2><a href="#articles">Read articles ↓</a></div>
        <div className="pulse-browse-groups">
          <details id="guides">
            <summary>Guides <span>Setup, payments and switching</span></summary>
            <RelatedPulseLinks articles={[]} links={guides} label="Guides" />
          </details>
          <details id="comparisons">
            <summary>Comparisons <span>Find the right fit for your club</span></summary>
            <RelatedPulseLinks articles={[]} links={comparisons} label="Comparisons and tools" />
          </details>
        </div>
      </nav>
      <section id="articles" aria-labelledby="articles-title">
        <h2 id="articles-title">Articles</h2>
        {featured && <section aria-labelledby="featured-title">
          <h3 id="featured-title" className="pulse-section-label">Featured</h3>
          <Article article={featured} small={false} />
        </section>}
        {articles.length > 0 && <section aria-labelledby="other-articles-title">
          <h3 id="other-articles-title" className="pulse-section-label">Other Articles</h3>
          <div className="pulse-card-grid">
            {articles.map(article => <Article key={article.id} article={article} small />)}
          </div>
        </section>}
        {loading && <Spinner />}
        {error && <p role="alert">Articles could not load. <button onClick={() => setRetry(value => value + 1)}>Try again</button></p>}
        {hasMore && !error && <button className="pulse-load-more" disabled={loading} onClick={() => setPage(value => value + 1)}>Load More</button>}
      </section>
    </div>
  );
}

function ArticlePage({ slug }) {
  const [article, setArticle] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let active = true;
    api.posts.read({ slug }, { formats: ['html', 'plaintext'], include: 'tags,authors' })
      .then(post => { if (active) setArticle(post); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [slug]);
  if (error) return <p className="pulse-error" role="alert">This article could not load. <Link to="/pulse/all">Back to Pulse</Link></p>;
  return article ? <><PulseMetadata article={article} /><ViewArticle article={article} /></> : <Spinner />;
}

export default function BlogsPage() {
  const dispatch = useDispatch();
  const { slug } = useParams();
  const location = useLocation();
  useEffect(() => {
    dispatch(setHeroImage(false));
    dispatch(setHeroTitle('ShuttleOps Pulse'));
    dispatch(setHeroContent('Stay on top of the latest ideas, insights, and tips for making the most of your badminton club. From smarter court rotations to player stats and club management hacks, Pulse is your go-to hub for turning club nights into smoother, fairer, and more enjoyable experiences—for managers and players alike.'));
  }, [dispatch]);
  useEffect(() => { document.getElementById('root')?.scrollTo(0, 0); }, [slug]);
  if (guidePathForSlug(slug)) return <Navigate to={`${guidePathForSlug(slug)}${location.search}${location.hash}`} replace />;
  if (!slug) return <Navigate to="/pulse/all" replace />;
  return slug === 'all' ? <ArticleCollection /> : <ArticlePage key={slug} slug={slug} />;
}
